import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { X } from "lucide-react";
import { setStoredVisitor } from "../lib/visitor";

const SESSION_DISMISS_PREFIX = "mr_popup_dismissed_session_";
const SUBSCRIBED_KEY = "mr_subscribed_v1";

function isSessionDismissed(id) {
  try { return window.sessionStorage.getItem(SESSION_DISMISS_PREFIX + id) === "1"; } catch { return false; }
}
function markSessionDismissed(id) {
  try { window.sessionStorage.setItem(SESSION_DISMISS_PREFIX + id, "1"); } catch { /* noop */ }
}
function isSubscribed() {
  // ONLY suppress if the visitor signed up through a marketing popup
  // on a prior visit. We deliberately do NOT consult the generic
  // visitor cookie (set by download captures, contact form, etc.) —
  // the spec is "popup re-appears next visit unless they sign up for
  // the mailing list right here".
  try { return window.localStorage.getItem(SUBSCRIBED_KEY) === "1"; } catch { return false; }
}
function markSubscribed() {
  try { window.localStorage.setItem(SUBSCRIBED_KEY, "1"); } catch { /* noop */ }
}
// Once any popup is closed/submitted in this session, suppress all
// remaining popups so a queue of 4 doesn't blast the visitor.
function isSessionWholeBlocked() {
  try { return window.sessionStorage.getItem("mr_popup_session_blocked") === "1"; } catch { return false; }
}
function markSessionWholeBlocked() {
  try { window.sessionStorage.setItem("mr_popup_session_blocked", "1"); } catch { /* noop */ }
}

/**
 * Renders any number of marketing popups, each with its own delay,
 * image, body, optional CTA, and optional newsletter signup form.
 *
 * Visibility rules (per the owner's brief):
 *   - Only show on the home page.
 *   - If the visitor has already subscribed → never show again.
 *   - If the visitor closes (X's) a popup → don't show that popup again
 *     this session; but next visit they will see it again unless they
 *     subscribe.
 *   - Closing OR submitting any popup blocks the rest of the queue for
 *     the remainder of the visit so the page doesn't get spammed.
 */
export default function MarketingPopups() {
  const loc = useLocation();
  const onHome = loc.pathname === "/" || loc.pathname === "";
  const [items, setItems] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!onHome) return;
    if (isSubscribed()) return;
    let alive = true;
    api.get("/marketing/popups")
      .then(({ data }) => { if (alive) setItems(Array.isArray(data) ? data : []); })
      .catch(() => { /* never crash a public page on this */ });
    return () => { alive = false; };
  }, [onHome]);

  // Schedule the popups in order of `position` honouring each delay.
  // Each timer only fires if (a) we're still on home, (b) no popup is
  // currently open, (c) the visitor hasn't been blocked for this session.
  useEffect(() => {
    if (!onHome || isSubscribed()) return;
    const timers = items.map((it) => {
      if (isSessionDismissed(it.id)) return null;
      const ms = Math.max(0, Number(it.delay_seconds || 0)) * 1000;
      return setTimeout(() => {
        if (isSessionWholeBlocked()) return;
        if (isSubscribed()) return;
        if (isSessionDismissed(it.id)) return;
        // Only one popup at a time. If something else is open, wait — a
        // future close will fire this re-check via state change below.
        setOpenId((cur) => cur || it.id);
      }, ms);
    });
    return () => timers.forEach((t) => t && clearTimeout(t));
  }, [items, onHome]);

  const current = useMemo(() => items.find((it) => it.id === openId) || null, [items, openId]);

  if (!onHome || !current) return null;

  const dismiss = () => {
    markSessionDismissed(current.id);
    markSessionWholeBlocked();
    setOpenId(null);
  };

  const submitSignup = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await api.post("/mailing-list", { email, source: `popup:${current.id}` });
      setStoredVisitor({ email });
      markSubscribed();
      markSessionWholeBlocked();
      toast.success("Thanks! See you in the inbox.");
      setOpenId(null);
    } catch {
      toast.error("Try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  const accent = current.accent_color || "#236f6b";

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4"
      data-testid={`marketing-popup-${current.id}`}
      onClick={dismiss}
    >
      <div
        className="bg-white rounded-[28px] max-w-md w-full p-0 overflow-hidden relative shadow-2xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={dismiss}
          className="absolute top-3 right-3 z-10 p-2 rounded-full bg-white/90 hover:bg-white shadow"
          aria-label="Close"
          data-testid="marketing-popup-close"
        >
          <X className="w-5 h-5" />
        </button>
        {current.image_url && (
          <div className="w-full bg-[#f4e4c6] grid place-items-center">
            <img
              src={current.image_url}
              alt=""
              className="block w-auto max-w-full max-h-[60vh] sm:max-h-[440px] object-contain"
            />
          </div>
        )}
        <div className="p-6">
          <h3
            className="font-accent text-2xl font-bold mb-1"
            style={{ color: accent }}
            data-testid="marketing-popup-title"
          >
            {current.title}
          </h3>
          {current.body && (
            <p className="text-[#4a5568] mb-4 whitespace-pre-line text-sm sm:text-base">
              {current.body}
            </p>
          )}

          {current.show_signup_form ? (
            <form onSubmit={submitSignup} className="space-y-3">
              <input
                required
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 rounded-full border-2 border-[#f4e4c6] focus:outline-none focus:border-[#7fcfc7]"
                data-testid="marketing-popup-email"
              />
              <button
                disabled={busy}
                className="btn-primary w-full justify-center"
                data-testid="marketing-popup-submit"
                style={{ background: accent }}
              >
                {busy ? "..." : (current.cta_label || "Catch the Wave")}
              </button>
            </form>
          ) : current.cta_url && current.cta_label ? (
            <a
              href={current.cta_url}
              target={current.cta_url.startsWith("http") ? "_blank" : undefined}
              rel="noreferrer"
              className="btn-primary w-full justify-center inline-flex"
              data-testid="marketing-popup-cta"
              style={{ background: accent }}
              onClick={() => { markSessionDismissed(current.id); markSessionWholeBlocked(); }}
            >
              {current.cta_label}
            </a>
          ) : null}
        </div>
      </div>
    </div>
  );
}
